export const TRIGGER = {
    click: 'click',
    hover: 'hover',
    custom: 'custom',
} as const;

type GetTriggerCallbackReturnType =
    | { onMouseEnter: (isOpen: boolean) => void; onMouseLeave: (isOpen: boolean) => void }
    | { onClick: (isOpen: boolean) => void }
    | Record<string, never>;

export interface UsePopoverProps {
    /**
     * Sets whether popover content is open
     */
    setIsOpen?: (isOpen: boolean) => void;
    /**
     * The interaction that triggers the popover.
     *
     * `hover` - means the popover will open when you hover with mouse or
     * focus with keyboard on the popover trigger
     *
     * `click` - means the popover will open on click or
     * press `Enter` to `Space` on keyboard
     */
    trigger?: keyof typeof TRIGGER;
}

/**
 * The usePopover hook is intended for internal use only and should not be exposed in the member app.
 *
 * usePopover's functionality will be expanded when implementing tooltip/modal/dropdown.
 */
export function usePopover(props: UsePopoverProps = {}): { getTriggerCallback: () => GetTriggerCallbackReturnType } {
    const { trigger, setIsOpen } = props;

    function getTriggerCallback(): GetTriggerCallbackReturnType {
        if (trigger === 'hover') {
            return {
                onMouseEnter: () => setIsOpen(true),
                onMouseLeave: () => setIsOpen(false),
            };
        }
        if (trigger === 'click') {
            return {
                onClick: () => setIsOpen(true),
            };
        }
        if (trigger === 'custom') {
            return {};
        }
    }

    return {
        getTriggerCallback,
    };
}
